import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import EmojiPicker from '../EmojiPicker';
import BadWordsModal from './BadWordsModal';
import {
  popoverContainerStyle,
  popoverHeaderStyle,
  popoverTextareaStyle,
  popoverButtonContainerStyle,
  getCancelButtonStyle,
  getSubmitButtonStyle
} from './PopoverStyles';
import { useLanguage } from '../../contexts/LanguageContext';
import { useBodyScrollLock } from '../../hooks';
import '../CreatePostPage.css'; // Issue #112: Import styles for emoji/camera buttons

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Image constants
const MAX_FILES = 10;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGE_DIMENSION = 1920;
const COMPRESSION_QUALITY = 0.85;

/**
 * Shared Edit Post Popover Component
 * Used in LandingPage and MyPosts for consistent edit functionality
 */
const EditPostPopover = ({ post, onClose, onSave }) => {
  const { t } = useLanguage();
  const editTextareaRef = useRef(null);
  const overlayRef = useRef(null);

  // Content state
  const [editContent, setEditContent] = useState('');

  // Image states
  const [existingImages, setExistingImages] = useState([]);
  const [deleteImageIds, setDeleteImageIds] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [isCompressing, setIsCompressing] = useState(false);

  // Emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Issue #161: Bad words modal state
  const [showBadWordsModal, setShowBadWordsModal] = useState(false);
  const [detectedBadWords, setDetectedBadWords] = useState([]);

  // Issue #463: Freeze the feed behind the popover, so the background can
  // neither be scrolled nor touched while the post is being edited.
  useBodyScrollLock(Boolean(post));

  // Issue #463: `useBodyScrollLock` only freezes the document, but the feed is
  // an inner scroll container (`.posts-feed` is `flex: 1` + `overflow-y: auto`),
  // so locking the body alone leaves it scrollable. Swallow every scroll gesture
  // that the popover itself cannot consume, which keeps the background still
  // regardless of which element is the actual scroller.
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!post || !overlay) return undefined;

    // Walk up from the gesture's target: if any box inside the popover can still
    // move in the requested direction, let the browser scroll it as usual.
    const canScrollInside = (target, deltaY) => {
      let node = target;
      while (node && node !== overlay) {
        if (node.scrollHeight > node.clientHeight &&
            /auto|scroll/.test(getComputedStyle(node).overflowY)) {
          // deltaY 0 means touch, where the direction is not known upfront —
          // defer to the browser and let `overscroll-behavior` stop the chaining.
          if (deltaY === 0) return true;
          const atTop = node.scrollTop <= 0;
          const atBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
          if (deltaY < 0 ? !atTop : !atBottom) return true;
        }
        node = node.parentElement;
      }
      return false;
    };

    const blockBackgroundScroll = (event) => {
      if (!canScrollInside(event.target, event.deltaY || 0)) {
        event.preventDefault();
      }
    };

    // Passive listeners cannot call preventDefault, so register explicitly.
    overlay.addEventListener('wheel', blockBackgroundScroll, { passive: false });
    overlay.addEventListener('touchmove', blockBackgroundScroll, { passive: false });
    return () => {
      overlay.removeEventListener('wheel', blockBackgroundScroll);
      overlay.removeEventListener('touchmove', blockBackgroundScroll);
    };
  }, [post]);

  // Initialize content and fetch existing images when post changes
  useEffect(() => {
    if (post) {
      setEditContent(post.post_content || '');
      fetchExistingImages();
    }
  }, [post]);

  // Issue #463: Grow the textarea with its content, but only up to the
  // `maxHeight` cap below — beyond that the field scrolls internally instead of
  // stretching the popover past the bottom of the screen.
  const autoSizeTextarea = (textarea) => {
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  // Issue #463: Re-size on every content change, which also covers the initial
  // render where an existing (potentially long) post is loaded into the field.
  useEffect(() => {
    autoSizeTextarea(editTextareaRef.current);
  }, [editContent]);

  const fetchExistingImages = async () => {
    if (!post?.id) return;
    try {
      const response = await axios.get(`/api/posts/${post.id}/images`);
      setExistingImages(response.data || []);
    } catch (err) {
      console.error('Failed to fetch post images:', err);
      setExistingImages([]);
    }
  };

  // Image compression function
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size <= MAX_FILE_SIZE) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
            if (width > height) {
              height = (height / width) * MAX_IMAGE_DIMENSION;
              width = MAX_IMAGE_DIMENSION;
            } else {
              width = (width / height) * MAX_IMAGE_DIMENSION;
              height = MAX_IMAGE_DIMENSION;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });

                if (compressedFile.size > MAX_FILE_SIZE) {
                  reject(new Error('Bild ist zu groß (auch nach Komprimierung)'));
                } else {
                  resolve(compressedFile);
                }
              } else {
                reject(new Error('Fehler beim Komprimieren des Bildes'));
              }
            },
            'image/jpeg',
            COMPRESSION_QUALITY
          );
        };
        img.onerror = () => reject(new Error('Fehler beim Laden des Bildes'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Fehler beim Lesen des Bildes'));
      reader.readAsDataURL(file);
    });
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);

    // Calculate total images (existing not deleted + new)
    const remainingExisting = existingImages.length - deleteImageIds.length;
    const totalImages = remainingExisting + newFiles.length + files.length;

    if (totalImages > MAX_FILES) {
      alert(`Maximal ${MAX_FILES} Bilder pro Post erlaubt`);
      return;
    }

    setIsCompressing(true);

    try {
      const validFiles = [];
      const previews = [];

      for (const file of files) {
        if (!ALLOWED_TYPES.includes(file.type)) {
          alert('Nur JPEG, PNG und WebP Bilder sind erlaubt');
          setIsCompressing(false);
          return;
        }

        try {
          const processedFile = await compressImage(file);

          if (processedFile.size > MAX_FILE_SIZE) {
            alert('Bild ist zu groß (max 5MB). Bitte wähle ein kleineres Bild.');
            setIsCompressing(false);
            return;
          }

          validFiles.push(processedFile);

          const reader = new FileReader();
          reader.onload = (event) => {
            previews.push({
              id: Math.random(),
              src: event.target.result,
              file: processedFile
            });

            if (previews.length === validFiles.length) {
              setNewPreviews(prev => [...prev, ...previews]);
              setNewFiles(prev => [...prev, ...validFiles]);
              setIsCompressing(false);
            }
          };
          reader.readAsDataURL(processedFile);
        } catch (compressionError) {
          alert(compressionError.message || 'Fehler beim Verarbeiten des Bildes');
          setIsCompressing(false);
          return;
        }
      }
    } catch (err) {
      alert('Fehler beim Verarbeiten der Dateien');
      setIsCompressing(false);
    }

    e.target.value = '';
  };

  // Remove new image
  const removeNewImage = (previewId) => {
    const index = newPreviews.findIndex(p => p.id === previewId);
    if (index > -1) {
      setNewPreviews(prev => prev.filter((_, i) => i !== index));
      setNewFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Mark existing image for deletion
  const removeExistingImage = (imageId) => {
    setDeleteImageIds(prev => [...prev, imageId]);
  };

  // Handle emoji selection
  const handleEmojiSelect = (emoji) => {
    if (editTextareaRef.current) {
      const start = editTextareaRef.current.selectionStart;
      const end = editTextareaRef.current.selectionEnd;
      const newContent = editContent.substring(0, start) + emoji + editContent.substring(end);
      setEditContent(newContent);

      // Set cursor position after emoji
      setTimeout(() => {
        editTextareaRef.current.focus();
        editTextareaRef.current.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    } else {
      setEditContent(editContent + emoji);
    }
    setShowEmojiPicker(false);
  };

  // Handle cancel
  const handleCancel = () => {
    setEditContent('');
    setExistingImages([]);
    setDeleteImageIds([]);
    setNewFiles([]);
    setNewPreviews([]);
    setIsCompressing(false);
    setShowEmojiPicker(false);
    onClose();
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!editContent.trim()) {
      alert('Beitrag kann nicht leer sein');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('content', editContent);

      // Add new images
      newFiles.forEach((file) => {
        formData.append('images', file);
      });

      // Add comma-separated list of image IDs to delete
      if (deleteImageIds.length > 0) {
        formData.append('delete_image_ids', deleteImageIds.join(','));
      } else {
        formData.append('delete_image_ids', '');
      }

      const response = await axios.put(`/api/posts/${post.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Notify parent of successful save
      onSave(response.data);

      // Reset state
      setEditContent('');
      setExistingImages([]);
      setDeleteImageIds([]);
      setNewFiles([]);
      setNewPreviews([]);
    } catch (err) {
      console.error('Failed to edit post:', err);
      // Issue #161: Check for bad words in response
      const badWords = err.response?.data?.bad_words;
      if (badWords && badWords.length > 0) {
        setDetectedBadWords(badWords);
        setShowBadWordsModal(true);
      } else {
        alert(err.response?.data?.message || 'Fehler beim Bearbeiten des Posts');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!post) return null;

  return (
    <div className="modal-overlay" ref={overlayRef} onClick={handleCancel}>
      {/* Issue #161: Bad Words Modal */}
      <BadWordsModal
        isOpen={showBadWordsModal}
        onClose={() => setShowBadWordsModal(false)}
        badWords={detectedBadWords}
        contentType="post"
      />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          ...popoverContainerStyle,
          // Issue #463: Keep the popover inside the viewport so the Save/Cancel
          // buttons stay reachable when a post also carries several images.
          maxHeight: '90vh',
          overflowY: 'auto',
          overscrollBehavior: 'contain'
        }}
      >
        <h2 style={popoverHeaderStyle}>{t('posts.editPost')}</h2>
        <textarea
          ref={editTextareaRef}
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          placeholder="Deine Gedanken.."
          autoFocus
          style={{
            ...popoverTextareaStyle,
            resize: 'none',
            minHeight: '80px',
            // Issue #463: Cap the field so a long post scrolls inside it rather
            // than pushing the popover (and its buttons) off-screen.
            maxHeight: '40vh',
            overflowX: 'hidden',
            overflowY: 'auto',
            // Issue #463: Stop touch scrolling from chaining out to the feed
            // once the field reaches its top/bottom.
            overscrollBehavior: 'contain'
          }}
        />

        {/* Emoji and File Upload Buttons - Issue #112: Match CreatePostPage styling */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <button
            type="button"
            className="create-post-action-button"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            title="Emoji hinzufügen"
            style={{ flex: 1 }}
          >
            <span className="action-button-icon">😊</span>
            <span className="action-button-text">{t('createPost.emoji') || 'Emoji'}</span>
          </button>

          {/* Issue #115: Changed label to button for keyboard accessibility */}
          <button
            type="button"
            className="create-post-action-button"
            title={isCompressing ? t('marketplace.compressing') : t('marketplace.addImages')}
            style={{ flex: 1 }}
            onClick={() => document.getElementById('edit-post-file-input')?.click()}
            disabled={isCompressing || isSubmitting}
            aria-label={t('marketplace.addImages') || 'Bilder hinzufügen'}
          >
            <span className="action-button-icon" aria-hidden="true">{isCompressing ? '⏳' : '📷'}</span>
            <span className="action-button-text">Bilder</span>
          </button>
          <input
            id="edit-post-file-input"
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            disabled={isCompressing || isSubmitting}
            className="create-post-file-input"
            aria-hidden="true"
          />
        </div>

        {/* Existing Images */}
        {existingImages.length > 0 && (
          <div className="image-previews">
            {existingImages
              .filter(img => !deleteImageIds.includes(img.id))
              .map(img => (
                <div key={img.id} className="preview-item" style={{ position: 'relative' }}>
                  <img
                    src={`${API_BASE_URL}${img.image_url}`}
                    alt="Existing"
                    className="preview-image"
                    style={{ pointerEvents: 'none', position: 'relative', zIndex: 1 }}
                  />
                  <button
                    type="button"
                    className="remove-image-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      removeExistingImage(img.id);
                    }}
                    title="Bild entfernen"
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      zIndex: 9999,
                      pointerEvents: 'auto'
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
          </div>
        )}

        {/* New Image Previews */}
        {newPreviews.length > 0 && (
          <div className="image-previews">
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '8px 0 4px 0' }}>
              Neue Bilder:
            </p>
            {newPreviews.map(preview => (
              <div key={preview.id} className="preview-item">
                <img src={preview.src} alt="Preview" className="preview-image" />
                <button
                  type="button"
                  className="remove-image-button"
                  onClick={() => removeNewImage(preview.id)}
                  title="Bild entfernen"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Emoji Picker */}
        {showEmojiPicker && (
          <EmojiPicker
            onEmojiSelect={handleEmojiSelect}
            onClose={() => setShowEmojiPicker(false)}
          />
        )}

        <div style={popoverButtonContainerStyle}>
          <button
            onClick={handleCancel}
            disabled={isSubmitting}
            style={getCancelButtonStyle(isSubmitting)}
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!editContent.trim() || isSubmitting || isCompressing}
            style={getSubmitButtonStyle(!editContent.trim() || isSubmitting || isCompressing)}
          >
            {isSubmitting ? t('posts.saving') : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPostPopover;
